/**
 * The settings panel for the selected widget: Data (source, metrics,
 * breakdown, filters, sort, limit, own period) and Display (title, text,
 * comparison, totals, sparkline, goal target). Every change goes straight
 * into the layout, so undo and auto-save see it like any other edit.
 */

import { useState, type ReactNode } from 'react';

import { DateControl } from './DateControl.tsx';
import { rangeLabel } from './format.ts';
import type {
  Catalogue,
  CatalogueSource,
  Filter,
  Widget,
  WidgetData,
  WidgetDisplay,
  WidgetResult,
} from './reports-api.ts';
import { ImageFields } from './ImageFields.tsx';
import { TitleFields } from './TitleFields.tsx';
import { RollupWidgetFields } from './RollupWidgetFields.tsx';
import { useRollup } from './rollup-context.ts';
import { NEEDS_DIMENSION, ONE_METRIC, VISUALS, VisualPicker } from './VisualPicker.tsx';

const DATA_TYPES: string[] = VISUALS.map((visual) => visual.type);

/** AgencyAnalytics' names for the widgets whose type name differs. */
const PANEL_NAME: Partial<Record<string, string>> = {
  HEADER: 'Title',
  TEXT: 'Textbox',
  TOC: 'Table of contents',
  METRIC: 'Stat',
  BAR: 'Column',
  HORIZONTAL_BAR: 'Horizontal',
};

interface Props {
  widget: Widget;
  catalogue: Catalogue;
  result: WidgetResult | undefined;
  onChange: (widget: Widget) => void;
  onClose: () => void;
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}): ReactNode {
  return (
    <label className="rs-check">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}

function FilterRows({
  source,
  filters,
  onChange,
}: {
  source: CatalogueSource;
  filters: Filter[];
  onChange: (filters: Filter[]) => void;
}): ReactNode {
  const dimensions = source.dimensions.filter((dimension) => dimension.key !== 'date');
  if (dimensions.length === 0) return null;
  const update = (index: number, patch: Partial<Filter>) =>
    onChange(filters.map((filter, at) => (at === index ? { ...filter, ...patch } : filter)));
  return (
    <fieldset className="rs-group">
      <legend>Filters</legend>
      {filters.map((filter, index) => (
        <div className="rs-filter" key={index}>
          <select
            aria-label="Filter field"
            value={filter.dimension}
            onChange={(event) => update(index, { dimension: event.target.value })}
          >
            {dimensions.map((dimension) => (
              <option key={dimension.key} value={dimension.key}>
                {dimension.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter rule"
            value={filter.op}
            onChange={(event) => update(index, { op: event.target.value as Filter['op'] })}
          >
            <option value="is">is</option>
            <option value="is_not">is not</option>
            <option value="contains">contains</option>
          </select>
          <input
            aria-label="Filter values, comma separated"
            value={filter.values.join(', ')}
            placeholder="value, value"
            onChange={(event) =>
              update(index, {
                values: event.target.value
                  .split(',')
                  .map((value) => value.trim())
                  .filter(Boolean),
              })
            }
          />
          <button
            type="button"
            className="linklike"
            aria-label="Remove filter"
            onClick={() => onChange(filters.filter((_, at) => at !== index))}
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className="linklike"
        onClick={() =>
          onChange([...filters, { dimension: dimensions[0]?.key ?? '', op: 'is', values: [] }])
        }
      >
        + Add filter
      </button>
    </fieldset>
  );
}

function DataTab({
  widget,
  catalogue,
  result,
  setData,
}: {
  widget: Widget;
  catalogue: Catalogue;
  result: WidgetResult | undefined;
  setData: (patch: Partial<WidgetData>) => void;
}): ReactNode {
  const source = catalogue.sources.find((item) => item.key === widget.data.source);
  const metrics = widget.data.metrics ?? [];
  const single = ONE_METRIC.includes(widget.type);
  const rollup = useRollup();
  const toggleMetric = (key: string) => {
    if (single) return setData({ metrics: [key] });
    setData({
      metrics: metrics.includes(key)
        ? metrics.filter((item) => item !== key)
        : [...metrics, key].slice(0, 8),
    });
  };
  return (
    <div className="rs-body">
      <label className="rs-field">
        Source
        <select
          value={widget.data.source ?? ''}
          onChange={(event) => {
            const next = catalogue.sources.find((item) => item.key === event.target.value);
            setData({
              source: event.target.value,
              metrics: next?.metrics[0] ? [next.metrics[0].key] : [],
              dimension: NEEDS_DIMENSION.includes(widget.type)
                ? (next?.dimensions.find((d) => d.key !== 'date')?.key ?? null)
                : null,
              filters: [],
            });
          }}
        >
          <option value="" disabled>
            Choose a source
          </option>
          {catalogue.sources.map((item) => (
            <option key={item.key} value={item.key}>
              {item.label}
            </option>
          ))}
        </select>
      </label>
      <RollupWidgetFields data={widget.data} setData={setData} />
      {source && (
        <>
          <fieldset className="rs-group">
            <legend>{single ? 'Metric' : 'Metrics'}</legend>
            <div className="rs-chips">
              {source.metrics.map((metric) => (
                <button
                  type="button"
                  key={metric.key}
                  className={`rs-chip${metrics.includes(metric.key) ? ' rs-chip-on' : ''}`}
                  aria-pressed={metrics.includes(metric.key)}
                  onClick={() => toggleMetric(metric.key)}
                >
                  {metric.label}
                </button>
              ))}
            </div>
          </fieldset>
          {widget.type !== 'METRIC' && widget.type !== 'GOAL' && (
            <label className="rs-field">
              Break down by
              <select
                value={widget.data.dimension ?? 'date'}
                onChange={(event) =>
                  setData({ dimension: event.target.value === 'date' ? null : event.target.value })
                }
              >
                {source.dimensions
                  .filter(
                    (dimension) =>
                      !(NEEDS_DIMENSION.includes(widget.type) && dimension.key === 'date'),
                  )
                  .map((dimension) => (
                    <option key={dimension.key} value={dimension.key}>
                      {dimension.label}
                    </option>
                  ))}
                {rollup && !widget.data.client_id && <option value="client">Client</option>}
              </select>
            </label>
          )}
          {widget.data.dimension && (
            <div className="rs-row">
              <label className="rs-field">
                Sort by
                <select
                  value={widget.data.sort?.metric ?? metrics[0] ?? ''}
                  onChange={(event) =>
                    setData({
                      sort: {
                        metric: event.target.value,
                        direction: widget.data.sort?.direction ?? 'desc',
                      },
                    })
                  }
                >
                  {metrics.map((key) => (
                    <option key={key} value={key}>
                      {source.metrics.find((metric) => metric.key === key)?.label ?? key}
                    </option>
                  ))}
                </select>
              </label>
              <label className="rs-field">
                Order
                <select
                  value={widget.data.sort?.direction ?? 'desc'}
                  onChange={(event) =>
                    setData({
                      sort: {
                        metric: widget.data.sort?.metric ?? metrics[0] ?? '',
                        direction: event.target.value as 'asc' | 'desc',
                      },
                    })
                  }
                >
                  <option value="desc">Highest first</option>
                  <option value="asc">Lowest first</option>
                </select>
              </label>
              <label className="rs-field rs-narrow">
                Rows
                <input
                  type="number"
                  min={1}
                  max={200}
                  value={widget.data.limit ?? 10}
                  onChange={(event) =>
                    setData({ limit: Math.max(1, Math.min(200, Number(event.target.value) || 10)) })
                  }
                />
              </label>
            </div>
          )}
          <FilterRows
            source={source}
            filters={widget.data.filters ?? []}
            onChange={(filters) => setData({ filters })}
          />
          <fieldset className="rs-group">
            <legend>Period</legend>
            <Check
              label="Use its own date range"
              checked={Boolean(widget.data.range)}
              onChange={(on) => setData({ range: on ? { preset: 'LAST_30_DAYS' } : null })}
            />
            {widget.data.range && (
              <DateControl range={widget.data.range} onRange={(range) => setData({ range })} />
            )}
          </fieldset>
          {result && result.state === 'OK' && (
            <p className="rs-explain">
              How this number is calculated: {source.label}, {rangeLabel(result.range)}
              {result.compare_range ? `, compared with ${rangeLabel(result.compare_range)}` : ''}
              {(widget.data.filters ?? []).length > 0
                ? `, ${(widget.data.filters ?? []).length} filter(s)`
                : ''}
              . Ratios are recalculated from the summed totals, never averaged per day.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function DisplayTab({
  widget,
  isMoney,
  setDisplay,
  setData,
}: {
  widget: Widget;
  isMoney: boolean;
  setDisplay: (patch: Partial<WidgetDisplay>) => void;
  setData: (patch: Partial<WidgetData>) => void;
}): ReactNode {
  const isData = DATA_TYPES.includes(widget.type);
  return (
    <div className="rs-body">
      {widget.type === 'HEADER' ? (
        <TitleFields display={widget.display} setDisplay={setDisplay} />
      ) : (
        <label className="rs-field">
          Title
          <input
            value={widget.display.title ?? ''}
            placeholder="Automatic"
            maxLength={120}
            onChange={(event) => setDisplay({ title: event.target.value })}
          />
        </label>
      )}
      {widget.type === 'TEXT' && (
        <label className="rs-field">
          Text
          <textarea
            rows={8}
            value={widget.display.text ?? ''}
            maxLength={4000}
            onChange={(event) => setDisplay({ text: event.target.value })}
          />
        </label>
      )}
      {widget.type === 'IMAGE' && <ImageFields display={widget.display} setDisplay={setDisplay} />}
      {widget.type === 'EMBED' && (
        <label className="rs-field">
          Address to embed (https)
          <input
            type="url"
            value={widget.display.url ?? ''}
            placeholder="https://"
            onChange={(event) => setDisplay({ url: event.target.value.trim() })}
          />
        </label>
      )}
      {(widget.type === 'GOAL' || widget.type === 'GAUGE') && (
        <label className="rs-field">
          Target (in the metric's unit; money in rand)
          <input
            type="number"
            min={0}
            value={widget.data.target === undefined ? '' : widget.data.target / (isMoney ? 100 : 1)}
            onChange={(event) =>
              setData({ target: Math.round(Number(event.target.value || 0) * (isMoney ? 100 : 1)) })
            }
          />
        </label>
      )}
      {isData && (
        <fieldset className="rs-group">
          <legend>Show</legend>
          <Check
            label="Comparison with the previous period"
            checked={widget.display.show_comparison !== false}
            onChange={(value) => setDisplay({ show_comparison: value })}
          />
          {widget.type === 'METRIC' && (
            <Check
              label="Trend line"
              checked={widget.display.show_sparkline !== false}
              onChange={(value) => setDisplay({ show_sparkline: value })}
            />
          )}
          {widget.type === 'TABLE' && (
            <Check
              label="Totals row"
              checked={widget.display.show_totals !== false}
              onChange={(value) => setDisplay({ show_totals: value })}
            />
          )}
        </fieldset>
      )}
    </div>
  );
}

export function WidgetSettings({ widget, catalogue, result, onChange, onClose }: Props): ReactNode {
  const isData = DATA_TYPES.includes(widget.type);
  const firstMetric = catalogue.sources
    .find((item) => item.key === widget.data.source)
    ?.metrics.find((metric) => metric.key === widget.data.metrics?.[0]);
  const isMoney = firstMetric?.format === 'money';
  const [tab, setTab] = useState<'data' | 'display'>(isData ? 'data' : 'display');
  const setData = (patch: Partial<WidgetData>) =>
    onChange({ ...widget, data: { ...widget.data, ...patch } });
  const setDisplay = (patch: Partial<WidgetDisplay>) =>
    onChange({ ...widget, display: { ...widget.display, ...patch } });
  return (
    <aside className="rs" aria-label="Widget settings">
      <header className="rs-head">
        <strong>{PANEL_NAME[widget.type] ?? widget.type.replace('_', ' ').toLowerCase()}</strong>
        <button type="button" className="rb-icon" aria-label="Close settings" onClick={onClose}>
          ×
        </button>
      </header>
      {isData && (
        <div className="rs-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'data'}
            className={tab === 'data' ? 'on' : ''}
            onClick={() => setTab('data')}
          >
            Data
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'display'}
            className={tab === 'display' ? 'on' : ''}
            onClick={() => setTab('display')}
          >
            Display
          </button>
        </div>
      )}
      {tab === 'data' && isData ? (
        <>
          <VisualPicker
            widget={widget}
            source={catalogue.sources.find((item) => item.key === widget.data.source)}
            onChange={onChange}
          />
          <DataTab widget={widget} catalogue={catalogue} result={result} setData={setData} />
        </>
      ) : (
        <DisplayTab widget={widget} isMoney={isMoney} setDisplay={setDisplay} setData={setData} />
      )}
    </aside>
  );
}
